import type { IItem, IMenuGroup, OrderablePath } from '@order-up/models';
import {
    findOrderablePathsForItem,
    isItemListedVisiblyInGroup,
    isItemVisibleOnline,
} from '@order-up/models';

/**
 * Rebuild a cart from an order the guest already placed.
 *
 * "Order again" is the cheapest repeat-purchase mechanic there is, and today
 * the button on the confirmation page navigates to an empty menu — the guest
 * re-picks everything by hand.
 *
 * WHY THIS IS NOT "COPY THE OLD LINES INTO THE CART"
 * -------------------------------------------------
 * A cart line carries a unit PRICE. Copying last month's price forward puts a
 * stale number in front of the guest, and the server re-prices at checkout —
 * so they are shown one number and charged another. That is the defect class
 * `menuPricingResolver` exists to prevent, arriving by a new door.
 *
 * So every line is re-resolved against the menu AS IT IS NOW, through the
 * canonical ROOT→LEAF resolver. The previous price is kept only to tell the
 * guest what changed.
 *
 * WHAT IT REFUSES TO GUESS
 * ------------------------
 * Matching is by item identity, never by name: two items can share a name
 * across menus and they are not the same item at the same price.
 *
 * Where several orderable paths disagree on price, the line is `needs_choice`
 * rather than silently tie-broken — `PathResolution.isAmbiguous` says it
 * outright: "the caller MUST ask the guest which menu they mean — picking one
 * silently mis-charges." The one exception is the path the guest ORIGINALLY
 * ordered from: if that group is still orderable it wins, because it is not a
 * guess, it is what they chose last time.
 *
 * An open-priced item ("market price") also needs a choice: there is no number
 * to put in a cart line, and 0 would read as free.
 */

/** One line of the order being repeated. Deliberately minimal and model-free. */
export interface PreviousOrderLine {
    readonly menuItemId: number;
    readonly name: string;
    readonly quantity: number;
    /** What they paid per unit last time, for the "price changed" message only. */
    readonly unitPrice: number;
    /** The group they ordered it from, if the order recorded one. */
    readonly menuGroupId?: number;
}

export type ReorderLineStatus =
    /** Still orderable at the same price — goes straight into the cart. */
    | 'available'
    /** Still orderable, but it costs something different now. */
    | 'price_changed'
    /** Orderable, but we must not pick the price for them. */
    | 'needs_choice'
    /**
     * Gone: the restaurant has removed it, or switched it off for online
     * ordering entirely. Saying so is a claim about the MENU.
     */
    | 'unavailable'
    /**
     * Still on the menu, just not orderable at this moment — every group that
     * lists it is outside its schedule, or off this channel right now.
     *
     * 🔴 THIS USED TO BE REPORTED AS `unavailable`, i.e. "no longer on the
     * menu". Those are different sentences and only one of them was ever true.
     * A guest told their favourite is GONE does not come back and try at
     * dinner; a guest told it is not available RIGHT NOW does. The planner has
     * always known the difference — the item is in hand and visible, it simply
     * has no live path — and threw it away.
     */
    | 'unavailable_now';

export interface ReorderLine {
    readonly status: ReorderLineStatus;
    readonly menuItemId: number;
    readonly name: string;
    readonly quantity: number;
    readonly previousUnitPrice: number;
    /** The price NOW. Absent when unavailable or needing a choice. */
    readonly currentUnitPrice?: number;
    /** The group this resolved through. Absent when unresolved. */
    readonly menuGroupId?: number;
}

export interface ReorderPlan {
    readonly lines: ReorderLine[];
    /** The lines that can be added without asking anything. */
    readonly addable: ReorderLine[];
    /** True when anything at all is not a clean repeat — drives the message. */
    readonly hasChanges: boolean;
}

/**
 * The single number this path would put in a cart line, or undefined.
 *
 * `OrderablePath` already carries the price the canonical resolver produced for
 * that walk, so nothing is re-derived here. Sizes and open pricing deliberately
 * return undefined: neither is one number, and a 0 would read as free.
 */
function flatPriceOf(path: OrderablePath): number | undefined {
    return path.price.kind === 'flat' ? path.price.unitPrice : undefined;
}

/**
 * @param timeZone the RESTAURANT's IANA zone. Menu schedules are written in the
 * restaurant's wall clock, and omitting this evaluates them in the DEVICE's —
 * so a guest whose phone is in another zone is told, wrongly, that items are
 * unavailable. `findOrderablePathsForItem` has always taken it; this planner
 * never passed it.
 */
export function buildReorderPlan(
    previous: ReadonlyArray<PreviousOrderLine>,
    items: ReadonlyArray<IItem>,
    menuGroups: ReadonlyArray<IMenuGroup>,
    now: Date = new Date(),
    timeZone?: string | null,
): ReorderPlan {
    const byId = new Map<number, IItem>();
    for (const item of items) {
        if (item.itemId != null) byId.set(item.itemId, item);
    }

    const groups = menuGroups as IMenuGroup[];
    const lines: ReorderLine[] = [];

    for (const line of previous) {
        const base = {
            menuItemId: line.menuItemId,
            name: line.name,
            quantity: line.quantity,
            previousUnitPrice: line.unitPrice,
        };

        const item = byId.get(line.menuItemId);
        // Gone from the menu, or pulled from sale entirely.
        if (!item) {
            lines.push({ ...base, status: 'unavailable' });
            continue;
        }

        const resolution = findOrderablePathsForItem(item, groups, now, timeZone);
        if (resolution.paths.length === 0) {
            // No live path has TWO meanings and they deserve different words.
            //
            // Still LISTED somewhere and sellable online, just not reachable at
            // this instant → a schedule or a channel is closed, and the honest
            // sentence is "not right now". Listed nowhere, or switched off for
            // online outright → it really has left this guest's menu.
            //
            // Availability is deliberately NOT consulted for the listing test:
            // that is the whole point, since it is what closed the path.
            const stillListed =
                isItemVisibleOnline(item) &&
                groups.some((g) => isItemListedVisiblyInGroup(g, line.menuItemId));
            lines.push({
                ...base,
                status: stillListed ? 'unavailable_now' : 'unavailable',
            });
            continue;
        }

        // The path they actually used last time, if it is still orderable.
        // Preferring it is not a tie-break — it is their own previous choice.
        const original =
            line.menuGroupId != null
                ? resolution.paths.find((p) => p.menuGroupId === line.menuGroupId)
                : undefined;

        const chosen = original ?? (resolution.isAmbiguous ? undefined : resolution.paths[0]);
        if (!chosen) {
            lines.push({ ...base, status: 'needs_choice' });
            continue;
        }

        const price = flatPriceOf(chosen);
        // Sizes or open pricing: there is no single number to put in the cart,
        // and 0 would read as free.
        if (price === undefined) {
            lines.push({ ...base, status: 'needs_choice', menuGroupId: chosen.menuGroupId });
            continue;
        }

        lines.push({
            ...base,
            status: price === line.unitPrice ? 'available' : 'price_changed',
            currentUnitPrice: price,
            menuGroupId: chosen.menuGroupId,
        });
    }

    const addable = lines.filter(
        (l) => l.status === 'available' || l.status === 'price_changed',
    );

    return {
        lines,
        addable,
        hasChanges: lines.some((l) => l.status !== 'available'),
    };
}
